// Social login (Phase 7) — mirrors api/src/http/routes/social-oauth.ts.
// The OAuth flow is a full-page browser navigation to the API origin (NOT a fetch):
// the API redirects to the provider, then back to the SPA with the result in the hash.
import { z } from "zod";
import { apiFetch } from "./client";

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

export const SOCIAL_PROVIDERS = ["google", "github", "microsoft", "slack"] as const;
export type SocialProvider = (typeof SOCIAL_PROVIDERS)[number];

export const PROVIDER_LABEL: Record<SocialProvider, string> = {
  google: "Google",
  github: "GitHub",
  microsoft: "Microsoft",
  slack: "Slack",
};

/** Full-page navigation target that begins the OAuth flow on the API origin. */
export function socialStartUrl(provider: SocialProvider): string {
  return `${API_BASE}/auth/oauth/${provider}/start`;
}

const providersSchema = z.object({ providers: z.array(z.enum(["google", "github", "microsoft", "slack"])) });

/** Which providers the API has configured — so the UI renders only real buttons. */
export function getEnabledProviders(signal?: AbortSignal): Promise<SocialProvider[]> {
  return apiFetch("/auth/oauth/providers", { schema: providersSchema, signal }).then((r) => r.providers);
}

export interface SocialSignupPrefill {
  provider: string;
  email: string | null;
  name: string | null;
}

// --- Authed link/unlink (Settings → Linked accounts) — attach a provider to the
//     signed-in account so a later "Sign in with X" resolves to it (no duplicate org). ---

export interface LinkedProvider {
  provider: SocialProvider;
  /** Provider-asserted email at link time (display only). */
  email: string | null;
  linkedAt: string;
}

const linkedSchema = z.object({
  identities: z.array(
    z.object({
      provider: z.enum(["google", "github", "microsoft", "slack"]),
      email: z.string().nullable(),
      linkedAt: z.string(),
    }),
  ),
});

/** The providers the signed-in admin has linked. */
export function getLinkedProviders(signal?: AbortSignal): Promise<LinkedProvider[]> {
  return apiFetch("/admin/oauth", { auth: true, schema: linkedSchema, signal }).then((r) => r.identities);
}

const startLinkSchema = z.object({
  startUrl: z.string().min(1).optional(),
  alreadyLinked: z.boolean().optional(),
});

/** Result of beginning a link: either a start URL to navigate to, or an already-linked short-circuit. */
export interface StartLinkResult {
  /** Top-level URL to navigate to (carries the short-lived, admin-bound link ticket). */
  startUrl?: string;
  /** The provider is already linked to this account → no OAuth round-trip is needed. */
  alreadyLinked?: boolean;
}

/**
 * Begin linking a provider to the signed-in account. Returns either a start URL the browser
 * must navigate to (it carries a short-lived, admin-bound ticket — the bearer can't ride the
 * OAuth redirect, so the server mints the ticket from this authed call), or `alreadyLinked`
 * when the provider is already attached (the server short-circuits to avoid a redundant
 * consent screen).
 */
export function startProviderLink(provider: SocialProvider): Promise<StartLinkResult> {
  return apiFetch(`/admin/oauth/${provider}/link/start`, { method: "POST", auth: true, schema: startLinkSchema });
}

/** Unlink a provider from the signed-in account. */
export function unlinkProvider(provider: SocialProvider): Promise<{ unlinked: boolean }> {
  return apiFetch(`/admin/oauth/${provider}`, { method: "DELETE", auth: true });
}

/**
 * Decode the (signed) social-signup handoff token for PREFILL ONLY — read its email +
 * name to pre-populate Register. The value is not trusted here; the API re-verifies the
 * token's signature on submit. Returns null for anything that isn't a social-signup token.
 */
export function decodeSocialSignup(token: string): SocialSignupPrefill | null {
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    // atob yields a binary string; decode it as UTF-8 so non-ASCII names (e.g. "José")
    // aren't mangled in the prefill. (Display-only — the API re-verifies the signature.)
    const bytes = Uint8Array.from(atob(padded), (ch) => ch.charCodeAt(0));
    const json = JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
    if (json.purpose !== "social-signup") return null;
    return {
      provider: typeof json.provider === "string" ? json.provider : "",
      email: typeof json.email === "string" ? json.email : null,
      name: typeof json.name === "string" ? json.name : null,
    };
  } catch {
    return null;
  }
}
